import { App, AutoComplete, Space, Spin, Tag, Tooltip } from "antd";
import { StyledDivWrapper } from "../addNewRule.style";
import { useRef, useState } from "react";
import { PHRASES_DICTIONARY } from "../../../../../entities/phrasesDictionary";
import validator from "../../../../../shared/validators/filterValueValidator";
import type {
  ChoosedOptionsType,
  HandleConfirmInputType,
  HandleDoubleClickType,
  ValueLabelItem,
} from "./ChoosedOptionsType";
import { SpinWrapper } from "../../../../../app/app.style";
import { LoadingOutlined } from '@ant-design/icons';

const getDisplayText = (
  valueFormat: ChoosedOptionsType["valueFormat"],
  item: string | ValueLabelItem
) => {
  if (valueFormat === "string") return item as string;
  const { value, label } = item as ValueLabelItem;
  return label ? `${value} (${label})` : value;
}

const getValue = (
  valueFormat: ChoosedOptionsType["valueFormat"],
  item: string | ValueLabelItem
) => {
  if (valueFormat === "string") return item as string;
  return (item as ValueLabelItem).value;
}

const ChoosedOptions: React.FC<ChoosedOptionsType> = (
  {
    name,
    valueFormat,
    inputValue,
    isLoadingAttributes,
    setInputValue,
    options,
    choosedValues,
    setChoosedValues,
  }
) => {

  const length = choosedValues.length;
  const resetTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [textTooltip, setTextTooltip] = useState(PHRASES_DICTIONARY.COPY.text_copy);

  const { message: messageApi } = App.useApp();

  const resetTooltipText = () => {
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
    setTextTooltip(PHRASES_DICTIONARY.COPY.text_copy);
  };

  const handleConfirmInput: HandleConfirmInputType = (option) => {
    const validValue = validator.validate(option.value);
    if (!validValue) return;

    if (valueFormat === "string") {
      const formatValue = option.label ? `${validValue} (${option.label})` : validValue;
      if (!choosedValues.includes(formatValue)) {
        setChoosedValues((prev) => [...prev, formatValue]);
      }
    } else {
      const exists = choosedValues.some((item) => item.value === validValue);
      if (!exists) {
        setChoosedValues((prev) => [
          ...prev,
          { value: validValue, label: option.label ?? validValue },
        ]);
      }
    }
    setInputValue("");
  };

  const handleDoubleClick: HandleDoubleClickType = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setTextTooltip(PHRASES_DICTIONARY.COPY.text_success_copy);
        resetTimeoutRef.current = setTimeout(() => resetTooltipText(), 2000);
      })
      .catch(() => {
        messageApi.error(PHRASES_DICTIONARY.COPY.text_error_copy);
      });
  };

  const removeItem = (item: string | ValueLabelItem) => {
    if (valueFormat === "string") {
      setChoosedValues((prev) =>
        prev.filter((f) => f !== item)
      );
    } else {
      setChoosedValues((prev) =>
        prev.filter((f) => f.value !== item.value)
      );
    }
  };

  return (
    <StyledDivWrapper $elLength={length}>
      <Space wrap className="tags" style={{ gap:"var(--spacing-xs)" }}>
        {choosedValues.map((item) => {
          const displayText = getDisplayText(valueFormat, item);
          const valueKey = getValue(valueFormat, item);
          return (
            <div key={valueKey} style={{ display: "flex", justifyContent: "center" }}>
              <Tooltip
                title={textTooltip}
                onOpenChange={(open) => {
                  if (!open) resetTooltipText();
                }}
              >
                <Tag
                  closable
                  onClose={() => removeItem(item)}
                  onDoubleClick={() => handleDoubleClick(valueKey)}
                  color="blue"
                >
                  {displayText}
                </Tag>
              </Tooltip>
            </div>
          );
        })}
      </Space>
      <AutoComplete
        placeholder={name}
        style={{ width: 400, flexShrink: 0 }}
        value={inputValue}
        options={options}
        onChange={(val) => setInputValue(val)}
        filterOption={(inputValue, option) => {
          if (!option?.value) return false;
          return option.value.toLowerCase().includes(inputValue.toLowerCase());
        }}
        onSelect={(_, option) => handleConfirmInput(option)}
        optionRender={(option) => (
          <span>
            {option.value}
            {option.label && ` (${option.label})`}
          </span>
        )}
        popupRender={(menu) =>
          isLoadingAttributes ? (
            <SpinWrapper style={{ justifyContent: "center" }}>
              <Spin indicator={<LoadingOutlined />} />
            </SpinWrapper>
          ) : (
            menu
          )
        }
      />
    </StyledDivWrapper>
  );
};

export default ChoosedOptions;
